import { useQuery } from '@tanstack/react-query';
import { firestore } from '../../../firebase';

export interface BaseFlowConfig {
  id: string;
  deviceId: string;
  companyName: string;
}

// Type for the flow device config
export interface FlowDeviceConfig extends BaseFlowConfig {
  cmLimit?: number;
  temp?: {
    alert: number;
    reset: number;
    alertMax?: number;
    resetMax?: number;
  };
}

// API function to get the config for a device
const getFlowDeviceConfig = async (deviceId: string): Promise<FlowDeviceConfig | null> => {
  if (!deviceId) {
    throw new Error('Device ID is required');
  }

  const snap = await firestore
    .collection('flowDeviceConfigs')
    .where('deviceId', '==', deviceId)
    .limit(1)
    .get();

  if (snap.empty) {
    return null;
  }

  const doc = snap.docs[0];
  return {
    id: doc.id,
    ...doc.data(),
  } as FlowDeviceConfig;
};

// Custom hook for getting the flow device config
export const useFlowConfig = (deviceId: string | null) => {
  const query = useQuery({
    queryKey: ['flowDeviceConfig', deviceId],
    queryFn: async (): Promise<FlowDeviceConfig | null> => {
      if (!deviceId) {
        return null;
      }
      return await getFlowDeviceConfig(deviceId);
    },
    enabled: !!deviceId,
    staleTime: 5 * 60 * 1000, // 5 minutes (config doesn't change often)
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  return {
    ...query,
    config: query.data,
  };
};
